import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  type Dispatch,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react';
import {
  DropzoneOptions,
  DropzoneState,
  FileError,
  useDropzone,
} from 'react-dropzone';

import { useControllableState } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled, WarningFilled } from '@mint-lib/icons';
import {
  RichTooltip,
  RichTooltipContent,
  RichTooltipTrigger,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import accepts from 'attr-accept';

import { typographyVariants } from '../../lib/typography.jsx';
import { ERRORS, getGlobalErrors } from './constants.js';
import { FileUploaderContent } from './FileUploaderContent.jsx';
import { FileUploaderItem } from './FileUploaderItem.jsx';
import { BaseFile, LocalFile, ReadyForUploadFile } from './types.js';
import {
  asyncCompose,
  createBaseFile,
  createBaseFileFromFileBlob,
  formatBytes,
  isFileError,
  isLocalFile,
  isLocalInvalidFile,
  isLocalValidFile,
  isPersistentFile,
  isReadyForUploadFile,
} from './utils.js';

export type FileUploaderV2RefType = {
  uploadFiles: (
    validFiles: ReadyForUploadFile[],
  ) => Promise<PromiseSettledResult<any>[]>;
  setFiles: Dispatch<SetStateAction<BaseFile[]>>;
};

export type FileUploaderV2RenderItemProps = {
  key: string;
  baseFile: BaseFile;
  onPreview: () => void;
  onRemove: () => void;
  onRetry: () => void;
  variant?: 'small' | 'medium';
};

export type FileUploaderProps = Omit<DropzoneOptions, 'multiple' | 'accept'> & {
  initialFiles?: Pick<BaseFile, 'name' | 'size' | 'previewUrl'>[];
  onChange?: (files: BaseFile[]) => void;
  validate?: (fileBlob: File) => Promise<null | FileError>;
  onUpload: (fileBlob: File) => Promise<any>;
  onDelete?: (file: BaseFile) => void;
  label?: string;
  helperText?: string;
  accept?: string[];
  totalSize?: number;
  variant?: 'area' | 'button' | 'default';
  itemVariant?: 'small' | 'medium';
  className?: string;
  fullWidth?: boolean;
  children?:
    | ReactNode
    | ((props: {
        getRootProps: DropzoneState['getRootProps'];
        getInputProps: DropzoneState['getInputProps'];
        isDragActive: DropzoneState['isDragActive'];
        open: DropzoneState['open'];
        disabled: boolean;
        variant?: 'area' | 'button' | 'default';
        buttonLabel?: string;
      }) => ReactNode);
  renderItem?: (props: FileUploaderV2RenderItemProps) => ReactNode;
  buttonLabel?: string;
};

export const FileUploader = forwardRef(
  (
    {
      onChange,
      initialFiles = [],
      validate,
      onUpload,
      onDelete,
      disabled,
      children = defaultRenderChildren,
      renderItem = defaultRenderItem,
      buttonLabel,
      minSize,
      maxSize,
      totalSize,
      maxFiles,
      accept,
      className,
      label,
      helperText,
      variant,
      itemVariant,
      fullWidth = false,
      ...dropzoneOptions
    }: FileUploaderProps,
    ref: Ref<FileUploaderV2RefType>,
  ) => {
    const { t } = useTranslation('@mint-lib/tw-components');

    const [files, setFiles] = useControllableState<BaseFile[]>({
      defaultProp: initialFiles.map((initialFile) =>
        createBaseFile({ ...initialFile, isUploaded: true }),
      ),
      onChange,
    });

    const updateFileById = (id: string, filePartial: Partial<BaseFile>) => {
      setFiles((previousFiles) => {
        const updatedFiles = [...previousFiles];
        const fileToUpdateIndex = updatedFiles.findIndex(
          (file) => file.cid === id,
        );
        updatedFiles[fileToUpdateIndex] = {
          ...updatedFiles[fileToUpdateIndex],
          ...filePartial,
        };
        return updatedFiles;
      });
    };

    const [rejectionError, setRejectionError] = useState<FileError | null>();

    const isMaxFilesReached = maxFiles && files.length >= maxFiles;
    const currentTotalSize = files.reduce((a, f) => a + (f.size ?? 0), 0);
    const isTotalSizeExceeded = Boolean(
      totalSize && currentTotalSize > totalSize,
    );
    const isUploading = files.some((file) => file.isUploading);
    const hasValidationError = files.some(({ validationError }) =>
      Boolean(validationError),
    );
    const hasUploadError = files.some(({ uploadError }) =>
      Boolean(uploadError),
    );

    const isDisabled =
      disabled || isMaxFilesReached || isTotalSizeExceeded || isUploading;

    const GLOBAL_ERRORS = getGlobalErrors({ maxFiles });

    const checkFilesForRejection = (
      filesToValidate: LocalFile[],
    ): FileError | null => {
      const isTooManyFiles =
        maxFiles && files.length + filesToValidate.length > maxFiles;

      if (isTooManyFiles) {
        return GLOBAL_ERRORS.tooManyFiles;
      }

      return null;
    };

    const resetValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      filesToValidate.forEach((file) => {
        file.validationError = null;
      });

      return filesToValidate;
    };

    const fileTypeValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      if (!accept?.length) {
        return filesToValidate;
      }

      const localValidFiles: LocalFile[] =
        filesToValidate.filter(isLocalValidFile);

      localValidFiles.forEach((file) => {
        const isAcceptable =
          file.fileBlob.type === 'application/x-moz-file' ||
          accepts(file.fileBlob, accept);

        if (!isAcceptable) {
          file.validationError = ERRORS.invalidType;
        }
      });

      return filesToValidate;
    };

    const fileSizeValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      if (!minSize && !maxSize) {
        return filesToValidate;
      }

      const localValidFiles: LocalFile[] =
        filesToValidate.filter(isLocalValidFile);

      localValidFiles.forEach((file) => {
        if (file.size) {
          if (minSize && file.size < minSize) {
            file.validationError = ERRORS.minSize;
          } else if (maxSize && file.size > maxSize) {
            file.validationError = ERRORS.maxSize;
          }
        }
      });

      return filesToValidate;
    };

    const duplicationValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      const localValidFiles: LocalFile[] =
        filesToValidate.filter(isLocalValidFile);

      localValidFiles.forEach((file) => {
        const firstIndex = filesToValidate.findIndex(
          (f) => f.name === file.name,
        );
        const lastIndex = filesToValidate.findLastIndex(
          (f) => f.name === file.name,
        );
        const isDuplicate = firstIndex !== lastIndex;
        if (isDuplicate) {
          file.validationError = ERRORS.fileDuplication;
        }
      });

      return filesToValidate;
    };

    const totalSizeValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      if (!totalSize) {
        return filesToValidate;
      }

      const persistentFiles = filesToValidate.filter(isPersistentFile);

      const persistentFilesSize = persistentFiles.reduce(
        (acc, persistentFile) => acc + (persistentFile?.size || 0),
        0,
      );

      let totalSizeAccumulator = persistentFilesSize;

      const localFiles = filesToValidate.filter(isLocalFile);
      localFiles.forEach((file) => {
        totalSizeAccumulator += file.size || 0;
        if (totalSizeAccumulator > totalSize) {
          file.validationError = ERRORS.totalSize;
        }
      });

      return filesToValidate;
    };

    const customValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      if (!validate) {
        return filesToValidate;
      }

      const localValidFiles: LocalFile[] =
        filesToValidate.filter(isLocalValidFile);
      const customValidationResults = await Promise.all(
        localValidFiles.map((file) => validate(file.fileBlob)),
      );

      localValidFiles.forEach((validFile, index) => {
        const validationResult = customValidationResults[index];
        if (isFileError(validationResult)) {
          validFile.validationError = validationResult;
        }
      });

      return filesToValidate;
    };

    const normalizeValidation = async (
      filesToValidate: BaseFile[],
    ): Promise<BaseFile[]> => {
      filesToValidate.forEach((file) => {
        if (file.validationError) {
          file.uploadError = null;
        }
      });
      return filesToValidate;
    };

    const validateFiles = async (filesToValidate: BaseFile[]) => {
      /*
        `fileTypeValidation` and `fileSizeValidation` are the copies
        of corresponding built-in validation functions in 'react-dropzone'
        We have to write them ourselves to keep the validation process simple
        otherwise we would've had "built-in" and "our" validators
        which we would've had to manage differently
      */

      const validators = [
        resetValidation,
        fileTypeValidation,
        fileSizeValidation,
        duplicationValidation,
        totalSizeValidation,
        customValidation,
        normalizeValidation,
      ];

      const validatedFiles = await asyncCompose(validators)(filesToValidate);

      const localInvalidFiles = validatedFiles.filter(isLocalInvalidFile);
      const localValidFiles = validatedFiles.filter(isLocalValidFile);
      const persistentFiles = validatedFiles.filter(isPersistentFile);

      setFiles([...localInvalidFiles, ...localValidFiles, ...persistentFiles]);

      return { localValidFiles };
    };

    const validateAndUploadFiles = async (filesToValidate: BaseFile[]) => {
      const { localValidFiles } = await validateFiles(filesToValidate);

      const readyForUploadFiles = localValidFiles.filter(isReadyForUploadFile);

      // if auto mode then start upload automagically
      if (!ref) {
        await uploadFiles(readyForUploadFiles);
      }
    };

    async function uploadFiles(filesToUpload: ReadyForUploadFile[]) {
      filesToUpload.forEach((fileToUpload) => {
        updateFileById(fileToUpload.cid, {
          isUploading: true,
        });
      });

      const fileUploadRequests = filesToUpload.map((fileToUpload) =>
        onUpload(fileToUpload.fileBlob),
      );

      fileUploadRequests.forEach((fileUploadRequest, index) =>
        fileUploadRequest
          .then(() => {
            updateFileById(filesToUpload[index].cid, {
              isUploaded: true,
              uploadError: null,
            });
          })
          .catch((error) => {
            updateFileById(filesToUpload[index].cid, {
              uploadError: isFileError(error) ? error : ERRORS.upload,
            });
          })
          .finally(() => {
            updateFileById(filesToUpload[index].cid, {
              isUploading: false,
            });
          }),
      );

      const response = await Promise.allSettled(fileUploadRequests);

      return response;
    }

    const handlePreviewFile = (previewUrl?: string) => () => {
      if (previewUrl) {
        window.open(previewUrl, '_blank');
      }
    };

    const handleRemoveFile = (fileToRemove: BaseFile) => () => {
      if (isPersistentFile(fileToRemove)) {
        onDelete?.(fileToRemove);
      }

      /*
        Some validations depend on other files (totalSize / duplications)
        if any file is removed, we need to revalidate all
      */
      const updatedFiles = files.filter(
        (file) => file.cid !== fileToRemove.cid,
      );
      validateFiles(updatedFiles);

      if (inputRef.current?.files) {
        const dt = new DataTransfer();
        const selectedFiles = Array.from(inputRef.current.files);
        for (const selectedFile of selectedFiles) {
          selectedFile.name !== fileToRemove.name && dt.items.add(selectedFile);
        }
        try {
          inputRef.current.files = dt.files;
        } catch {}
      }
    };

    const handleRetryFile = (fileToRetry: BaseFile) => () => {
      if (isLocalFile(fileToRetry)) {
        try {
          updateFileById(fileToRetry.cid, {
            isUploading: true,
            uploadError: null,
          });
          onUpload?.(fileToRetry.fileBlob);
          updateFileById(fileToRetry.cid, {
            isUploaded: true,
          });
        } catch (error) {
          updateFileById(fileToRetry.cid, {
            uploadError: isFileError(error) ? error : ERRORS.upload,
          });
        } finally {
          updateFileById(fileToRetry.cid, {
            isUploading: false,
          });
        }
      }
    };

    const { getRootProps, getInputProps, isDragActive, open, inputRef } =
      useDropzone({
        ...dropzoneOptions,
        disabled: isDisabled,
        accept,
        multiple: maxFiles !== 1,
        onDrop: (acceptedFiles, fileRejections) => {
          const selectedFiles = [
            ...acceptedFiles,
            ...fileRejections.map(({ file }) => file),
          ].map((fileBlob) => createBaseFileFromFileBlob(fileBlob));

          const rejectionCheckResult = checkFilesForRejection(selectedFiles);
          setRejectionError(rejectionCheckResult);

          if (isFileError(rejectionCheckResult)) {
            return;
          }

          validateAndUploadFiles([...selectedFiles, ...files]);
        },
      });

    const fileItems = files.map((baseFile) => {
      const fileItemElement = renderItem({
        key: baseFile.cid,
        baseFile,
        variant: itemVariant,
        onPreview: handlePreviewFile(baseFile.previewUrl),
        onRemove: handleRemoveFile(baseFile),
        onRetry: handleRetryFile(baseFile),
      });

      return fileItemElement;
    });

    const getTooltipTitle = () => {
      if (isMaxFilesReached) {
        return 'Maximum amount of files has been uploaded';
      }
      if (isTotalSizeExceeded) {
        return 'Total allowed size exceeded';
      }
      if (isUploading) {
        return 'Please wait until ongoing file loading completes';
      }

      return null;
    };

    const tooltipTitle = getTooltipTitle();
    const rootClassName = [className, isDragActive && 'isDragActive']
      .filter(Boolean)
      .join(' ');

    const [totalSizeDisplayValue = 0, totalSizeDisplaySuffix] = totalSize
      ? formatBytes(totalSize)
      : [];
    const currentTotalSizeDisplayValue =
      totalSize &&
      ((currentTotalSize / totalSize) * totalSizeDisplayValue).toFixed(0);

    useImperativeHandle(ref, () => ({
      uploadFiles,
      setFiles,
    }));

    return (
      <div
        data-testid="file-uploader"
        data-size={fullWidth ? 'fullWidth' : 'default'}
        aria-disabled={isDisabled}
        className={cn(
          'flex w-full flex-col gap-2 text-text-secondary aria-[disabled]:text-text-disabled data-[size="default"]:max-w-[470px] data-[size="fullWidth"]:max-w-none',
          rootClassName,
        )}
      >
        {!!label && (
          <p className={typographyVariants({ variant: 'label01' })}>{label}</p>
        )}
        <RichTooltip>
          <RichTooltipTrigger
            onFocus={(event) => {
              event.preventDefault();
            }}
          >
            <div>
              {typeof children === 'function' ? (
                children({
                  getRootProps,
                  getInputProps,
                  isDragActive,
                  disabled: isDisabled,
                  open,
                  variant,
                  buttonLabel,
                })
              ) : (
                <div
                  aria-disabled={disabled}
                  data-state={isDragActive}
                  className="flex h-20 cursor-pointer items-center justify-center bg-ui-primary outline-none aria-[disabled]:text-text-disabled"
                  {...getRootProps({
                    isDragActive,
                    disabled,
                  })}
                >
                  <input {...getInputProps()} />
                  {children}
                </div>
              )}
            </div>
          </RichTooltipTrigger>
          {tooltipTitle && (
            <RichTooltipContent side="right" description={tooltipTitle} />
          )}
        </RichTooltip>
        {!!helperText && (
          <p className={typographyVariants({ variant: 'helperText01' })}>
            {helperText}
          </p>
        )}
        {Number(maxFiles) > 1 && (
          <p className={typographyVariants({ variant: 'helperText01' })}>
            {t('Max. files:')} {maxFiles}
          </p>
        )}
        {!!accept?.length && (
          <p className={typographyVariants({ variant: 'helperText01' })}>
            {t('Supported files:')} {accept.join(', ')}
          </p>
        )}
        {!!maxSize && (
          <p className={typographyVariants({ variant: 'helperText01' })}>
            {t('Max. file size:')} {formatBytes(maxSize).join('')}
          </p>
        )}
        {!!totalSize && (
          <p className={typographyVariants({ variant: 'helperText01' })}>
            Uploaded files size: {currentTotalSizeDisplayValue} /{' '}
            {`${totalSizeDisplayValue}${totalSizeDisplaySuffix}`}
          </p>
        )}

        {hasValidationError && (
          <div className="flex items-center gap-1">
            <WarningAltFilled className="fill-warning-main" />
            <p
              className={typographyVariants({
                variant: 'helperText01',
                className: 'text-warning-dark',
              })}
            >
              {GLOBAL_ERRORS.hasValidationError.message}
            </p>
          </div>
        )}
        {!!isTotalSizeExceeded && (
          <div className="flex items-center gap-1">
            <WarningAltFilled className="fill-warning-main" />
            <p
              className={typographyVariants({
                variant: 'helperText01',
                className: 'text-warning-dark',
              })}
            >
              {GLOBAL_ERRORS.totalSize.message}
            </p>
          </div>
        )}
        {rejectionError && (
          <div className="flex items-center gap-1">
            <WarningAltFilled className="fill-warning-main" />
            <p
              className={typographyVariants({
                variant: 'helperText01',
                className: 'text-warning-dark',
              })}
            >
              {rejectionError.message}
            </p>
          </div>
        )}
        {hasUploadError && (
          <div className="flex items-center gap-1">
            <WarningFilled className="fill-error-main" />
            <p
              className={typographyVariants({
                variant: 'helperText01',
                className: 'text-error-dark',
              })}
            >
              {GLOBAL_ERRORS.hasUploadError.message}
            </p>
          </div>
        )}
        {!!fileItems?.length && (
          <ul className="order-first m-0 flex flex-col gap-2 p-0 md:order-last lg:order-last xl:order-last 2xl:order-last">
            {fileItems}
          </ul>
        )}
      </div>
    );
  },
);

const defaultRenderChildren: NonNullable<FileUploaderProps['children']> = (
  props,
) => <FileUploaderContent {...props} />;

const defaultRenderItem: NonNullable<FileUploaderProps['renderItem']> = ({
  key,
  ...restProps
}) => <FileUploaderItem key={key} {...restProps} />;

export default FileUploader;
